<template>
  <v-dialog
    v-model="localShow"
    max-width="560px"
    persistent
    @click:outside="handleCancel"
    @keydown.esc="handleCancel"
  >
    <v-card class="assign-incident-dialog">
      <v-card-title class="d-flex align-center pa-4">
        <v-icon icon="mdi-account-plus" size="24" class="mr-3" />
        <div class="flex-grow-1">
          <h2 class="text-h6 mb-1">Asignar handler</h2>
          <p class="text-caption text-grey mb-0">
            Incidencia #{{ incident?.id }} — {{ incident?.title }}
          </p>
        </div>
        <v-btn icon="mdi-close" variant="text" size="small" @click="handleCancel" />
      </v-card-title>

      <v-card-text class="pa-6">
        <!--
          P13-V1C — asignación CANÓNICA de usuario (assign-user). La lista es la proyección ADVISORY
          del servidor (GET /:id/eligible-assignees): jefes de departamento ACTIVOS con membresía
          vigente del departamento ENRUTADO de la incidencia. Este diálogo no calcula elegibilidad,
          no filtra por estado local y no concede nada: la operación re-bloquea y revalida el destino
          dentro de su transacción, y un 403/409 del servidor es la respuesta de autoridad real.
          (El diálogo anterior apuntaba a un endpoint fantasma POST /:id/assign — nunca existió — con
          una lista de "supervisores" filtrada por un campo que el servicio jamás poblaba, y recogía
          un comentario y un checkbox de notificación que ningún consumidor leía.)
        -->
        <v-alert type="info" variant="tonal" class="mb-4" density="comfortable">
          El destino debe ser un jefe activo del departamento enrutado. La lista es orientativa: el
          servidor revalida la elegibilidad al confirmar.
        </v-alert>

        <v-select
          v-model="selectedTargetId"
          :items="assignees"
          :loading="loading"
          item-title="name"
          item-value="id"
          label="Usuario destino"
          :no-data-text="loading ? 'Cargando asignables…' : 'Sin asignables elegibles para esta incidencia'"
          :rules="[v => !!v || 'Seleccione un usuario destino']"
          variant="outlined"
        >
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps" :subtitle="item.raw.email" />
          </template>
        </v-select>
      </v-card-text>

      <v-card-actions class="pa-4 pt-0">
        <v-spacer />
        <v-btn variant="text" @click="handleCancel">Cancelar</v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!selectedTargetId || loading"
          @click="handleAssign"
        >
          Asignar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Incident, EligibleAssignee } from '@/types/incidents'

const props = withDefaults(defineProps<{
  modelValue: boolean
  incident?: Incident | null
  /** Proyección advisory del servidor (eligible-assignees). Nunca autoridad local. */
  assignees?: EligibleAssignee[]
  loading?: boolean
}>(), {
  incident: null,
  assignees: () => [],
  loading: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  /** targetUserId numérico — el ÚNICO dato que viaja a la operación assign-user. */
  assign: [targetUserId: number]
  cancel: []
}>()

const localShow = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v),
})

const selectedTargetId = ref<number | null>(null)

watch(() => props.modelValue, (open) => {
  if (open) selectedTargetId.value = null
})

function handleAssign() {
  if (!selectedTargetId.value) return
  emit('assign', selectedTargetId.value)
}

function handleCancel() {
  emit('cancel')
  emit('update:modelValue', false)
}
</script>

<style scoped>
.assign-incident-dialog {
  border-radius: 12px;
}
</style>
